// src/redux/savedCreatorsSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { listSavedCreators, toggleSavedCreator } from "../services/savedCreatorsService";

const toStr = (v) => String(v ?? "").trim();

const getCreatorId = (it) =>
  toStr(
    it?.creatorId ||
      it?.external_id ||
      it?.externalId ||
      it?.id ||
      it?.channelId ||
      it?.keyId ||
      it?._routeId ||
      it?.username ||
      ""
  );

const unwrapListResponse = (resp) => {
  if (Array.isArray(resp)) return { items: resp, count: resp.length, last_key: null };
  const root = resp?.data || resp?.body || resp || {};

  const items = Array.isArray(root.items)
    ? root.items
    : Array.isArray(resp?.items)
    ? resp.items
    : [];

  const count = Number.isFinite(root.count)
    ? Number(root.count)
    : Number.isFinite(resp?.count)
    ? Number(resp.count)
    : items.length;

  const last_key = root.last_key ?? resp?.last_key ?? null;
  return { items, count, last_key };
};

const unwrapToggleResponse = (resp, fallbackCreatorId) => {
  const root = resp?.data || resp?.body || resp || {};
  const creatorId = toStr(
    root.creatorId || root.external_id || root.externalId || root.id || fallbackCreatorId || ""
  );
  const saved = root.saved === true || root.saved === "true";
  return { creatorId, saved };
};

export const listSavedCreatorsAsync = createAsyncThunk(
  "savedCreators/list",
  async (params = {}, { rejectWithValue }) => {
    try {
      const resp = await listSavedCreators(params);
      return resp || {};
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load saved creators");
    }
  }
);

export const toggleSavedCreatorAsync = createAsyncThunk(
  "savedCreators/toggle",
  async ({ creatorId, prevSaved } = {}, { rejectWithValue }) => {
    const cid = toStr(creatorId);
    if (!cid) return rejectWithValue({ message: "creatorId is required", creatorId: cid, prevSaved });

    try {
      const res = await toggleSavedCreator({ creatorId: cid });
      return { raw: res, creatorId: cid };
    } catch (e) {
      return rejectWithValue({
        message: e?.message || "Failed to toggle saved",
        creatorId: cid,
        prevSaved,
      });
    }
  }
);

const initialState = {
  savedSet: {},
  items: [],
  count: 0,
  lastKey: null,

  loading: false,
  loaded: false,
  loadedAt: null,
  error: null,

  pendingRemoved: {}, // rollback buffer
  togglingById: {},
};

const savedCreatorsSlice = createSlice({
  name: "savedCreators",
  initialState,
  reducers: {
    clearSavedError(state) {
      state.error = null;
    },
    clearSavedState() {
      return { ...initialState };
    },
  },
  extraReducers: (b) => {
    // list
    b.addCase(listSavedCreatorsAsync.pending, (st) => {
      st.loading = true;
      st.error = null;
    });

    b.addCase(listSavedCreatorsAsync.fulfilled, (st, a) => {
      st.loading = false;
      st.loaded = true;
      st.loadedAt = Date.now();
      st.error = null;

      const { items, count, last_key } = unwrapListResponse(a.payload);

      const nextSet = {};
      const nextItems = [];

      for (const it of items) {
        const id = getCreatorId(it);
        if (!id) continue;
        nextSet[id] = true;
        nextItems.push(it);
      }

      st.savedSet = nextSet;
      st.items = nextItems;
      st.count = Number.isFinite(count) ? Number(count) : nextItems.length;
      st.lastKey = last_key || null;
    });

    b.addCase(listSavedCreatorsAsync.rejected, (st, a) => {
      st.loading = false;
      st.loaded = false;
      st.error = a.payload || a.error?.message || "Failed to load saved creators";
    });

    // optimistic toggle
    b.addCase(toggleSavedCreatorAsync.pending, (st, a) => {
      const { creatorId, prevSaved } = a.meta.arg || {};
      const id = toStr(creatorId);
      if (!id) return;

      st.togglingById[id] = true;

      const current = st.savedSet[id] === true;
      const wasSaved = typeof prevSaved === "boolean" ? prevSaved : current;

      if (wasSaved) {
        const arr = Array.isArray(st.items) ? st.items : [];
        const idx = arr.findIndex((x) => getCreatorId(x) === id);

        if (idx >= 0) {
          st.pendingRemoved[id] = { item: arr[idx], index: idx };
          st.items = arr.filter((_, i) => i !== idx);
        }

        delete st.savedSet[id];
      } else {
        st.savedSet[id] = true;
      }

      st.error = null;
    });

    b.addCase(toggleSavedCreatorAsync.fulfilled, (st, a) => {
      const fallbackId = toStr(a.payload?.creatorId);
      const parsed = unwrapToggleResponse(a.payload?.raw, fallbackId);
      const id = toStr(parsed.creatorId);
      if (!id) return;

      delete st.togglingById[id];
      delete st.pendingRemoved[id];

      const saved = parsed.saved === true;

      if (saved) st.savedSet[id] = true;
      else delete st.savedSet[id];

      if (!saved) {
        st.items = (Array.isArray(st.items) ? st.items : []).filter((x) => getCreatorId(x) !== id);
      }

      st.error = null;
    });

    b.addCase(toggleSavedCreatorAsync.rejected, (st, a) => {
      const p = a.payload || {};
      const id = toStr(p.creatorId);
      if (!id) {
        st.error = p?.message || a.error?.message || "Failed to toggle saved";
        return;
      }

      delete st.togglingById[id];

      // rollback set
      if (p.prevSaved === true) st.savedSet[id] = true;
      else if (p.prevSaved === false) delete st.savedSet[id];

      // rollback list
      if (p.prevSaved === true) {
        const info = st.pendingRemoved?.[id];
        if (info?.item) {
          const arr = Array.isArray(st.items) ? st.items : [];
          const exists = arr.some((x) => getCreatorId(x) === id);
          if (!exists) {
            const insertAt = Number.isFinite(info.index) ? info.index : arr.length;
            st.items = [
              ...arr.slice(0, Math.max(0, insertAt)),
              info.item,
              ...arr.slice(Math.max(0, insertAt)),
            ];
          }
        }
      }

      delete st.pendingRemoved[id];
      st.error = p.message || "Failed to toggle saved";
    });
  },
});

export const { clearSavedError, clearSavedState } = savedCreatorsSlice.actions;
export default savedCreatorsSlice.reducer;

// selectors (NOW correct because store mounts `savedCreators`)
export const selectIsSaved = (state, creatorId) =>
  Boolean(state.savedCreators?.savedSet?.[toStr(creatorId)]);

export const selectSavedCreators = (state) => state.savedCreators?.items || [];
export const selectSavedCreatorsLoading = (state) => state.savedCreators?.loading || false;
export const selectSavedCreatorsError = (state) => state.savedCreators?.error || null;
export const selectSavedCreatorsLastKey = (state) => state.savedCreators?.lastKey || null;
export const selectSavedCreatorsTogglingById = (state) => state.savedCreators?.togglingById || {};